import React, { useMemo, useState } from 'react';
import Loader from '../../../components/Loader';
import useBatteryDelivered from '../../../hooks/batteries/useBatteryDelivered';
import DeliveryChart from '../charts/DeliveryChart';
import DeliveryDashboard from '../charts/DeliveryDashboard';
import EnduranceChart from '../charts/EnduranceChart';
import { DELIVERY_SERIES_COLOR, STATE_GREY_SWATCH_OOS, STATE_GREY_SWATCH_UNMEASURED, fmtAge, fmtDate, fmtStamp } from '../lib/chartTheme';
import { toRows } from '../lib/deliveryChartRows';
import { aggregateDays, groupWeeks, summarizeDays } from '../lib/deliveryDashboardV1';
import { RESPONDED_SCORE_THRESHOLD, summarizeDelivery } from '../lib/deliverySummaryV1';
import { committedProduct, fcrActivatedSummary, marketsWithCommitment } from '../lib/marketReportsV1';
import { RECORD_STARTS_UTC, pastRange } from '../lib/recordWindow';
import { Panel, StatTile } from './primitives';

/**
 * Window widths, in days. The report opens on the last 24 hours — the question a delivery
 * panel is opened with is "how did it go today" — and the week is one click away. At a month
 * or a quarter the per-period chart gives way to the day calendar + weekly bars.
 */
const RANGES = [1, 7, 30, 90];

const rangeLabel = days => (days === 1 ? '24h' : `${days}d`);
const rangeWords = days => (days === 1 ? '24 hours' : `${days} days`);

/** Below this share of committed periods backed, the Backed tile warns. */
const BACKED_WARN = 0.9;
/** Below this share of activated periods responded, the Responded tile warns. */
const RESPONDED_WARN = 0.8;
const THRESHOLD_PCT = Math.round(RESPONDED_SCORE_THRESHOLD * 100);

const LegendChip = ({ label, color, colors }) => (
    <span className="vb-legend__chip">
        <span className="vb-legend__swatches" aria-hidden>
            {(colors ?? [color]).map(c => (
                <span key={c} className="vb-legend__swatch" style={{ background: c }} />
            ))}
        </span>
        {label}
    </span>
);

/**
 * Settled delivery reports per period: committed power, minimum available capacity, and the
 * setpoint following score. Four tiles, one chart per committed market (or, at 30 d / 90 d,
 * the calendar and the weekly bars), a summary line and a legend. Every range is clamped at
 * the record start.
 */
const DeliveryReport = ({ batteryId }) => {
    const [days, setDays] = useState(1);
    // `pastRange` reads the clock, so it is memoised per width rather than called during
    // render — a fresh `to` on every render would refetch on every render.
    const range = useMemo(() => pastRange(days), [days]);
    const { reports, isReady, error, chunkCount, truncatedChunks } = useBatteryDelivered(batteryId, range);

    const dashboard = days >= 30;
    const dayset = useMemo(() => {
        if (!dashboard) {
            return null;
        }
        const aggregated = aggregateDays(reports, Date.parse(range.from), Date.parse(range.to));
        return { days: aggregated, weeks: groupWeeks(aggregated) };
    }, [dashboard, reports, range]);
    // Tiles and the summary line come from the raw reports on the 24 h / 7 d path, and from
    // the same day set the calendar and bars draw on the dashboard path (the two are the same
    // figures — tested).
    const stats = useMemo(() => (dayset ? summarizeDays(dayset.days, committedProduct(reports)) : summarizeDelivery(reports)), [dayset, reports]);
    const showChart = reports.length >= 2;

    const body = () => {
        if (!isReady) {
            return <Loader />;
        }
        if (error) {
            return <p className="gateway-section__empty">Could not load the delivery reports.</p>;
        }
        if (showChart && stats?.marketReportCount === 0) {
            // Reports exist, but not one carries a market report: the commitment and
            // availability the panel is about were never written for this window.
            return <p className="gateway-section__empty">{stats.reportCount} settled reports, none with a market report.</p>;
        }
        if (showChart && dayset) {
            return (
                <>
                    <DeliveryDashboard weeks={dayset.weeks} />
                    <div className="vb-legend">
                        <LegendChip label="Responded" color={DELIVERY_SERIES_COLOR.responded} />
                        <LegendChip label="Standing by" color={DELIVERY_SERIES_COLOR.standby} />
                        <LegendChip label="Missed" color={DELIVERY_SERIES_COLOR.missed} />
                        <LegendChip label="Not backed" color={STATE_GREY_SWATCH_OOS} />
                        <LegendChip label="Responded share" color={DELIVERY_SERIES_COLOR.score} />
                        {stats && stats.outOfServiceCount > 0 ? <LegendChip label="Out of service" color={STATE_GREY_SWATCH_OOS} /> : null}
                        {stats && stats.unmeasuredCount > 0 ? <LegendChip label="Unmeasured" color={STATE_GREY_SWATCH_UNMEASURED} /> : null}
                    </div>
                </>
            );
        }
        if (showChart) {
            // One chart per committed market: an up-and-down battery is two — FCR-D up and
            // FCR-D down, each carrying the capacity the writer copied into its direction.
            // With nothing committed in the window there is still one battery-wide chart.
            const chartMarkets = marketsWithCommitment(reports);
            return (
                <>
                    {(chartMarkets.length > 0 ? chartMarkets : [null]).map(market => {
                        // The market's own settled figures: how often it was actually
                        // activated, and the endurance behind its bid. Null / absent while
                        // the backend does not send the per-market fields yet — the chart
                        // then falls back to the battery-wide curves alone.
                        const activated = market === null ? null : fcrActivatedSummary(reports, market);
                        const marketRows = market === null ? null : toRows(reports, market);
                        const hasEndurance = marketRows !== null && marketRows.some(row => row.enduranceH !== null);
                        return (
                            <div key={market?.key ?? 'battery'} className="vb-chart">
                                {chartMarkets.length > 1 ? (
                                    <h3 className="vb-chart__market" style={{ color: market.color }}>
                                        {market.label}
                                    </h3>
                                ) : null}
                                {activated !== null ? (
                                    <p className="vb-summary">
                                        FCR activated periods: {activated.activatedCount} of {activated.measuredCount} measured
                                        {market !== null && chartMarkets.length <= 1 ? ` · ${market.label}` : ''}
                                    </p>
                                ) : null}
                                <DeliveryChart reports={reports} from={range.from} to={range.to} market={market} />
                                {hasEndurance ? (
                                    <>
                                        <h4 className="vb-chart__market">Endurance</h4>
                                        <EnduranceChart rows={marketRows} from={range.from} to={range.to} />
                                    </>
                                ) : null}
                            </div>
                        );
                    })}
                    <div className="vb-legend">
                        <LegendChip label="Activated" color={DELIVERY_SERIES_COLOR.activated} />
                        {stats && stats.outOfServiceCount > 0 ? <LegendChip label="Out of service" color={STATE_GREY_SWATCH_OOS} /> : null}
                        {stats && stats.unmeasuredCount > 0 ? <LegendChip label="Unmeasured" color={STATE_GREY_SWATCH_UNMEASURED} /> : null}
                        <span className="vb-legend__note">
                            Washes behind the chart: Activated marks the FCR activated periods. Click a legend entry on the chart to hide a series.
                        </span>
                    </div>
                </>
            );
        }
        if (reports.length === 1) {
            return <p className="gateway-section__empty">One settled report in the last {rangeWords(days)}.</p>;
        }
        return <p className="gateway-section__empty">No settled reports in the last {rangeWords(days)}.</p>;
    };

    return (
        <Panel
            icon="fa-chart-line"
            title="Delivery report"
            info={
                dashboard
                    ? 'Settled reports per period. Calendar: one cell per UTC day, red where an FCR activated period was missed, green by share responded, teal where standing by. Bars: capacity sold per ISO week (MW·h) and its outcome; responded share on the right axis.'
                    : 'Settled reports per period, one chart per market: committed power, minimum available capacity, and the setpoint following score, with the FCR activated periods washed behind them. Times are UTC.'
            }
            right={
                <span className="gateway-telemetry__controls vb-ranges" role="group" aria-label="Delivery report window">
                    {RANGES.map(d => (
                        <button
                            key={d}
                            type="button"
                            aria-pressed={d === days}
                            className={`gateway-telemetry__preset ${d === days ? 'gateway-telemetry__preset--active' : ''}`}
                            onClick={() => setDays(d)}
                        >
                            {rangeLabel(d)}
                        </button>
                    ))}
                </span>
            }
        >
            <div className="vb-page__tiles vb-page__tiles--4">
                <StatTile
                    label="Commitment"
                    // A measured zero and an unmeasured window are different answers, and
                    // "0.0 kW" is the more plausible-looking of the two.
                    value={stats && stats.marketReportCount > 0 ? (stats.meanCommittedMw * 1000).toFixed(1) : '—'}
                    unit={stats && stats.marketReportCount > 0 ? 'kW' : undefined}
                    info="Mean committed power over the periods with a market report."
                    hint={
                        !stats
                            ? undefined
                            : stats.marketReportCount === 0
                            ? 'no market report in window'
                            : stats.committedProduct === null
                            ? `over ${stats.marketReportCount} periods`
                            : `${stats.committedProduct.label} sold in ${stats.committedCount + stats.committedUnmeasuredCount} of ${
                                  stats.marketReportCount
                              } periods`
                    }
                />
                <StatTile
                    label="Backed"
                    value={stats?.backedRate != null ? `${(stats.backedRate * 100).toFixed(0)}%` : '—'}
                    tone={stats?.backedRate != null && stats.backedRate < BACKED_WARN ? 'bad' : undefined}
                    info="Share of committed periods where the minimum available power reached the commitment. Unmeasured periods are excluded; out-of-service periods count as not backed."
                    hint={
                        stats?.backedRate != null
                            ? `${stats.backedCount} of ${stats.committedCount} committed periods` +
                              (stats.committedOutOfServiceCount > 0 ? ` · ${stats.committedOutOfServiceCount} out of service` : '') +
                              (stats.committedUnmeasuredCount > 0 ? ` · ${stats.committedUnmeasuredCount} unmeasured` : '')
                            : stats?.marketReportCount === 0
                            ? 'no market report in window'
                            : stats && stats.committedUnmeasuredCount > 0
                            ? `all ${stats.committedUnmeasuredCount} committed periods unmeasured`
                            : 'nothing committed in window'
                    }
                />
                <StatTile
                    label="Activated"
                    value={stats && stats.measuredCount > 0 ? `${((stats.activatedCount / stats.measuredCount) * 100).toFixed(0)}%` : '—'}
                    info="Share of measured periods in which the FCR service was activated — the grid frequency left the dead band in the market's activation direction: below 49.9 Hz for FCR-D up, above 50.1 Hz for FCR-D down."
                    hint={
                        stats && stats.measuredCount > 0
                            ? `${stats.activatedCount} of ${stats.measuredCount} measured periods`
                            : stats
                            ? 'no measured period in window'
                            : undefined
                    }
                />
                <StatTile
                    label="Responded"
                    value={stats?.respondedRate != null ? `${(stats.respondedRate * 100).toFixed(0)}%` : '—'}
                    tone={stats?.respondedRate != null && stats.respondedRate < RESPONDED_WARN ? 'bad' : undefined}
                    info={`Share of FCR activated periods whose setpoint following score exceeds ${THRESHOLD_PCT}%.`}
                    hint={
                        stats?.respondedRate != null
                            ? `FCR activated in ${stats.activatedCount} of ${stats.measuredCount} measured periods. ` +
                              `${stats.respondedCount} of ${stats.activatedCount} exceed a setpoint following score of ${THRESHOLD_PCT}%.`
                            : stats && stats.measuredCount > 0
                            ? `FCR activated in 0 of ${stats.measuredCount} measured periods.`
                            : 'no measured period in window'
                    }
                />
            </div>
            {stats ? (
                <p className="vb-summary">
                    {stats.reportCount} settled {stats.reportCount === 1 ? 'report' : 'reports'} · {fmtStamp(stats.firstEnd)} →{' '}
                    {fmtStamp(stats.lastEnd)} · latest {fmtAge(Date.now() - stats.lastEnd)} ago
                    {stats.outOfServiceCount > 0 ? ` · ${stats.outOfServiceCount} out of service` : ''}
                    {stats.unmeasuredCount > 0 ? ` · ${stats.unmeasuredCount} unmeasured` : ''}
                    {truncatedChunks > 0 ? ` · reports missing in ${truncatedChunks} of ${chunkCount} sub-windows` : ''}
                </p>
            ) : null}
            {body()}
            {range.clamped ? <p className="gw-card__foot">The record starts {fmtDate(RECORD_STARTS_UTC)}; the window is cut there.</p> : null}
        </Panel>
    );
};

export default DeliveryReport;
